import {
  useCountryAllWarehouseListQuery,
  useSupplierShippingRatesCreateMutation,
  useSupplierShippingRatesUpdateMutation,
} from 'api';
import { Form } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import { useParams } from 'react-router-dom';
import { getUpdatedPayload } from 'utils/helpers';
import { FormInstance, FormProps } from 'components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { Price, SupplierSpecialRate } from '..';
import { IRatesAddEditModal } from './index';

const keysToSkip: string[] = [
  'edit_measurementRange',
  'edit_metrics',
  'edit_price',
  'measurementRange',
  'metrics',
  'price',
];

export type LeadTime = 'DAY' | 'WEEK';

export const useRatesAddEditModal = ({ dataToEdit, onModalStateChange, modalState }: IRatesAddEditModal) => {
  const [editId, setEditId] = useState<string | null>(null);
  const [prices, setPrices] = useState<Price[]>([]);
  const [disableSaveBtn, setDisableSaveBtn] = useState<boolean>(true);
  const [triggerValidation, setTriggerValidation] = useState(false);
  const [type, setType] = useState<LeadTime>('WEEK');
  const [form] = Form.useForm();
  const metrics = useWatch('metrics', form);
  const measurementRange = useWatch('measurementRange', form);
  const price = useWatch('price', form);

  const { id } = useParams();
  const { handleSuccess, handleError } = useNotificationMessages();
  const { data: warehouseData = [] } = useCountryAllWarehouseListQuery();
  const [createShippingRate, { isLoading: createIsLoading }] = useSupplierShippingRatesCreateMutation();
  const [updateShippingRate, { isLoading: updateIsLoading }] = useSupplierShippingRatesUpdateMutation();

  const initialData = useMemo(
    () =>
      dataToEdit
        ? {
            ...dataToEdit,
            warehouseId: dataToEdit.warehouse.id,
          }
        : undefined,
    [dataToEdit]
  );
  const handleTypeChange = useCallback((type: LeadTime) => {
    setDisableSaveBtn(false);
    setType(type);
  }, []);

  const isChanged = useMemo(() => {
    return [metrics, measurementRange, price].some((value) => value !== undefined && value !== '');
  }, [metrics, measurementRange, price]);

  const clearFieldValidation = useCallback((form: FormInstance, field: string) => {
    const hasError = form.getFieldError(field)?.length;
    if (hasError) {
      form.setFields([
        {
          name: field,
          errors: [], // Clears the error for filed
        },
      ]);
    }
  }, []);

  const onPriceRowCancel = useCallback(() => setEditId(null), []);
  const onPriceRowEdit = useCallback(
    (measurementRange: string) => {
      setEditId(measurementRange);
      const priceRowData = prices.find((_price) => _price.measurementRange === measurementRange);
      if (priceRowData) {
        form.setFieldsValue({
          edit_metrics: priceRowData.metrics,
          edit_measurementRange: priceRowData.measurementRange,
          edit_price: priceRowData.price,
        });
      }
    },
    [prices, form]
  );

  const onPriceRowSave = useCallback(
    async (measurementRange: string) => {
      try {
        await form.validateFields(['edit_metrics', 'edit_measurementRange', 'edit_price']);
        const { edit_metrics, edit_measurementRange, edit_price } = form.getFieldsValue([
          'edit_metrics',
          'edit_measurementRange',
          'edit_price',
        ]);
        setPrices((prevState) =>
          prevState.map((price) => {
            if (price.measurementRange === measurementRange) {
              return {
                ...price,
                metrics: edit_metrics,
                measurementRange: edit_measurementRange,
                price: edit_price,
              };
            } else {
              return price;
            }
          })
        );

        form.resetFields(['edit_metrics', 'edit_measurementRange', 'edit_price']);
        setEditId(null);
      } catch {
        // handle catch
      }
    },
    [form]
  );
  const onPriceRowRemove = useCallback((measurementRange: string) => {
    setPrices((prevState) => prevState.filter((_price) => _price.measurementRange !== measurementRange));
    setDisableSaveBtn(false);
  }, []);

  const onPriceSave = useCallback(async () => {
    setTriggerValidation(true);
    try {
      await form.validateFields(['metrics', 'measurementRange', 'price']);
      const { metrics, measurementRange, price } = form.getFieldsValue(['metrics', 'measurementRange', 'price']);
      setPrices((prevState) => [
        ...prevState,
        {
          metrics,
          measurementRange,
          price,
        },
      ]);
      form.resetFields(['metrics', 'measurementRange', 'price']);
      setTriggerValidation(false);
    } catch {
      // handle catch
    }
  }, [form]);

  const onPriceCancel = useCallback(() => {
    form.resetFields(['metrics', 'measurementRange', 'price']);
    setTriggerValidation(false);
  }, [form]);

  const onCancel = useCallback(() => {
    form.resetFields();
    onModalStateChange();
    setPrices([]);
    setEditId(null);
    setDisableSaveBtn(true);
  }, [form, onModalStateChange]);

  const onFinish: FormProps<SupplierSpecialRate>['onFinish'] = useCallback(
    async (values: SupplierSpecialRate) => {
      const payload = getUpdatedPayload(values, initialData, keysToSkip);
      const priceItems = form.getFieldsValue(['metrics', 'measurementRange', 'price']);
      const isAllItems = Object.values(priceItems || {}).every((_item) => !!_item);

      if (initialData) {
        payload.id = initialData.id;
      }
      payload.supplierId = id;
      payload.prices = prices;
      payload.timeUnit = type;
      if (isAllItems) {
        payload.prices = [...payload.prices, priceItems];
      }
      try {
        const { data, error } = await (initialData ? updateShippingRate(payload) : createShippingRate(payload));
        if (data?.success) {
          handleSuccess(`Shipping Rate was successfully ${initialData ? 'updated' : 'created'}.`);
          onCancel();
        } else {
          handleError(error);
        }
      } catch (err) {
        handleError(err);
      }
    },
    [initialData, createShippingRate, id, updateShippingRate, prices, onCancel, form, handleSuccess, handleError, type]
  );

  useEffect(() => {
    if (initialData && modalState) {
      form.setFieldsValue(initialData);
      if (initialData.prices) {
        setPrices(initialData.prices);
      }
      if (initialData.timeUnit) {
        setType(initialData.timeUnit);
      }
    }
  }, [initialData, form, modalState]);
  return {
    onPriceRowRemove,
    onPriceRowCancel,
    onPriceRowSave,
    onPriceRowEdit,
    onPriceSave,
    onCancel,
    onFinish,
    form,
    editId,
    triggerValidation,
    warehouseData,
    prices,
    isChanged,
    onPriceCancel,
    isLoading: createIsLoading || updateIsLoading,
    disableSaveBtn,
    type,
    handleTypeChange,
    clearFieldValidation,
  };
};
